import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { api } from "../lib/api";
import { dateShort, money } from "../lib/format";
import { Loader2, Receipt } from "lucide-react";

/**
 * Drill-down sheet for a single UPS spend bucket (Freight, Fuel surcharge,
 * Accessorials / fines, Adjustments, Informational, Other).
 *
 * Props:
 *   reportId   – string
 *   bucket     – string | null (when null, sheet is closed)
 *   range      – { from, to, period } (controls which lines load)
 *   onClose    – fn
 */
export default function BucketLinesSheet({ reportId, bucket, range, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!bucket) {
      setData(null);
      return;
    }
    setLoading(true);
    api
      .upsLines(reportId, bucket, range?.from, range?.to)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [reportId, bucket, range?.from, range?.to]);

  const lines = data?.lines || [];

  return (
    <Sheet open={!!bucket} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        className="w-full sm:max-w-3xl overflow-y-auto"
        data-testid="bucket-lines-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-2xl flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#525252]" />
            {bucket}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-2 text-xs text-[#525252] font-mono-tabular">
          {range?.period}
          {range?.from && range?.to
            ? ` · ${dateShort(range.from)} → ${dateShort(range.to)}`
            : ""}
        </div>

        {loading ? (
          <div className="py-10 text-sm text-[#525252] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading lines…
          </div>
        ) : !data ? (
          <div className="py-10 text-sm text-[#DC2626]">Couldn't load lines.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 mt-5">
              <div className="border border-[#E5E5E5] rounded-md p-3 bg-[#FAFAFA]">
                <div className="eyebrow mb-1">Lines</div>
                <div className="font-display text-2xl font-bold font-mono-tabular">
                  {data.count}
                </div>
              </div>
              <div className="border border-[#E5E5E5] rounded-md p-3 bg-[#FAFAFA]">
                <div className="eyebrow mb-1">Total billed</div>
                <div className="font-display text-2xl font-bold font-mono-tabular">
                  {money(data.total_billed)}
                </div>
              </div>
            </div>

            <div className="mt-5">
              {lines.length === 0 ? (
                <div className="border border-dashed border-[#E5E5E5] rounded-md p-6 text-center text-sm text-[#525252]">
                  No lines for this bucket in the selected period.
                </div>
              ) : (
                <div className="overflow-x-auto scroll-thin">
                  <table className="w-full text-sm" data-testid="bucket-lines-table">
                    <thead>
                      <tr className="border-b border-[#E5E5E5] text-left">
                        <th className="py-2 pr-3 eyebrow">Tracking</th>
                        <th className="py-2 px-3 eyebrow">Invoice</th>
                        <th className="py-2 px-3 eyebrow">Date</th>
                        <th className="py-2 px-3 eyebrow">Description</th>
                        <th className="py-2 pl-3 text-right eyebrow">Billed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((l, i) => (
                        <tr
                          key={`${l.tracking_number}-${l.invoice_number}-${i}`}
                          className="border-b border-[#F0F0EF] hover:bg-[#FAFAFA]"
                          data-testid={`bucket-line-${i}`}
                        >
                          <td className="py-2 pr-3 font-mono-tabular text-[12px]">
                            {l.tracking_number}
                          </td>
                          <td className="py-2 px-3 font-mono-tabular text-[12px] text-[#525252]">
                            {l.invoice_number || "—"}
                          </td>
                          <td className="py-2 px-3 font-mono-tabular text-[12px] text-[#525252]">
                            {dateShort(l.invoice_date) || dateShort(l.pickup_date) || "—"}
                          </td>
                          <td className="py-2 px-3 text-[12px] text-[#525252] max-w-xs truncate">
                            {l.charge_description ||
                              l.section ||
                              l.service ||
                              l.charge_class ||
                              "—"}
                          </td>
                          <td className="py-2 pl-3 text-right font-mono-tabular text-[13px] font-medium">
                            {money(l.billed_charge)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
